import { Box, InputAdornment, MenuItem, Select, TextField, Typography } from '@mui/material'
import { ChevronDownIcon, ChevronUpIcon } from '@untitled-theme/icons-react'
import { useState } from 'react'

import CupertinoSwitch from '@/shared/components/CupertinoSwitch'

import {
  HEATING_FUNDING_PERIODS,
  HEATPUMP_CALCULATOR,
} from '../../constants/funding-overview.constants'
import { calculateHeatpumpFunding } from '../../utils/heatpumpFunding'

const FIELD_LABEL_SX = {
  fontSize: '10px',
  fontWeight: 600,
  color: '#6A6E78',
  marginBottom: '4px',
}

const RESULT_ROW_SX = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  marginBottom: '6px',
}

const formatEuro = (value: number) => `${new Intl.NumberFormat('de-DE').format(value)} €`

const COST_STEP = 1000

const STEPPER_ARROW_SX = {
  display: 'flex',
  cursor: 'pointer',
  color: '#464A54',
  '&:hover': { color: '#181D29' },
}

const StepperArrows = ({ onStep }: { onStep: (direction: 1 | -1) => void }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', marginLeft: '2px' }}>
    <Box onClick={() => onStep(1)} sx={STEPPER_ARROW_SX}>
      <ChevronUpIcon style={{ width: 12, height: 12 }} />
    </Box>
    <Box onClick={() => onStep(-1)} sx={STEPPER_ARROW_SX}>
      <ChevronDownIcon style={{ width: 12, height: 12 }} />
    </Box>
  </Box>
)

const OptionSelect = ({
  options,
  selectedIndex,
  onSelect,
}: {
  options: string[]
  selectedIndex: number
  onSelect: (index: number) => void
}) => (
  <Select
    size="small"
    fullWidth
    value={selectedIndex}
    onChange={event => onSelect(Number(event.target.value))}
    sx={{ fontSize: '12px', '& .MuiSelect-select': { padding: '6px 10px' } }}
  >
    {options.map((label, index) => (
      <MenuItem key={label} value={index} sx={{ fontSize: '12px' }}>
        {label}
      </MenuItem>
    ))}
  </Select>
)

const HeatpumpCalculatorCard = () => {
  const {
    title,
    subtitle,
    baseRate,
    speedBonusByPeriod,
    maxEligibleCostsByPeriod,
    additionalUnitCaps,
    bonusHint,
    incomeTiers,
  } = HEATPUMP_CALCULATOR
  const [periodIndex, setPeriodIndex] = useState(0)
  const [tierIndex, setTierIndex] = useState(0)
  const [withChild, setWithChild] = useState(false)
  const [cost, setCost] = useState(HEATPUMP_CALCULATOR.defaultCost)
  const [units, setUnits] = useState(1)

  const tier = incomeTiers[tierIndex]
  const speedBonus = speedBonusByPeriod[periodIndex]
  const {
    uncappedRate,
    rate,
    maxEligibleCosts,
    eligibleCosts,
    fundingFirstUnit,
    fundingFurtherUnits,
    funding,
    costsAfterFunding,
  } = calculateHeatpumpFunding({
    cost,
    units,
    baseRate,
    speedBonus,
    incomeBonus: tier.bonus,
    capRate: tier.capRate,
    firstUnitCap: maxEligibleCostsByPeriod[periodIndex],
    additionalUnitCaps,
  })

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: '260px',
        border: '1px solid #E1E1E4',
        borderRadius: '16px',
        padding: '20px',
      }}
    >
      <Typography sx={{ fontWeight: 600, fontSize: '13px' }}>{title}</Typography>
      <Typography
        sx={{ fontSize: '10px', color: '#6A6E78', marginTop: '2px', marginBottom: '12px' }}
      >
        {subtitle}
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '10px 12px', marginBottom: '10px' }}>
        <Box sx={{ flex: '2 1 100px', minWidth: 0 }}>
          <Typography sx={FIELD_LABEL_SX}>Zeitraum</Typography>
          <OptionSelect
            options={HEATING_FUNDING_PERIODS.map(period => period.label)}
            selectedIndex={periodIndex}
            onSelect={setPeriodIndex}
          />
        </Box>
        <Box sx={{ flex: '3 1 95px', minWidth: 0 }}>
          <Typography sx={FIELD_LABEL_SX}>Kosten</Typography>
          <TextField
            size="small"
            fullWidth
            value={cost === 0 ? '' : new Intl.NumberFormat('de-DE').format(cost)}
            onChange={event => setCost(Number(event.target.value.replace(/\D/g, '')) || 0)}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    €
                    <StepperArrows
                      onStep={direction =>
                        setCost(value => Math.max(0, value + direction * COST_STEP))
                      }
                    />
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              '& input': { fontSize: '12px', padding: '6px 6px 6px 10px' },
              '& .MuiOutlinedInput-root': { paddingRight: '8px' },
            }}
          />
        </Box>
        <Box sx={{ flex: '1 1 60px', minWidth: 0 }}>
          <Typography sx={FIELD_LABEL_SX}>W.E.</Typography>
          <TextField
            size="small"
            fullWidth
            value={units}
            onChange={event =>
              setUnits(Math.max(1, Number(event.target.value.replace(/\D/g, '')) || 1))
            }
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <StepperArrows
                      onStep={direction => setUnits(value => Math.max(1, value + direction))}
                    />
                  </InputAdornment>
                ),
              },
            }}
            sx={{ '& input': { fontSize: '12px', padding: '6px 10px' } }}
          />
        </Box>
      </Box>

      <Box sx={{ marginBottom: '12px' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '4px',
          }}
        >
          <Typography sx={{ ...FIELD_LABEL_SX, marginBottom: 0 }}>
            Zu versteuerndes Haushaltseinkommen (€)
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Typography sx={{ fontSize: '10px', color: '#464A54' }}>mit Kind</Typography>
            <CupertinoSwitch
              checked={withChild}
              onChange={event => setWithChild(event.target.checked)}
            />
          </Box>
        </Box>
        <OptionSelect
          options={incomeTiers.map(t => (withChild ? t.labelWithChild : t.label))}
          selectedIndex={tierIndex}
          onSelect={setTierIndex}
        />
      </Box>

      <Box sx={RESULT_ROW_SX}>
        <Typography sx={{ fontSize: '12px', color: '#464A54' }}>Grundfördersatz:</Typography>
        <Typography sx={{ fontSize: '12px', fontWeight: 500 }}>{baseRate} %</Typography>
      </Box>
      <Box sx={RESULT_ROW_SX}>
        <Typography sx={{ fontSize: '12px', color: '#464A54' }}>Geschwindigkeitsbonus:</Typography>
        <Typography sx={{ fontSize: '12px', fontWeight: 500 }}>{speedBonus} %</Typography>
      </Box>
      <Box sx={RESULT_ROW_SX}>
        <Typography sx={{ fontSize: '12px', color: '#464A54' }}>Einkommensbonus:</Typography>
        <Typography sx={{ fontSize: '12px', fontWeight: 500 }}>{tier.bonus} %</Typography>
      </Box>
      <Box sx={RESULT_ROW_SX}>
        <Typography sx={{ fontSize: '12px', color: '#464A54' }}>
          {units > 1 ? 'Fördersatz 1. WE:' : 'Fördersatz gesamt:'}
        </Typography>
        <Typography sx={{ fontSize: '12px', fontWeight: 500 }}>
          {rate} %{' '}
          {uncappedRate > tier.capRate && (
            <Box component="span" sx={{ fontSize: '10px', color: '#6A6E78' }}>
              (Deckel {tier.capRate} %)
            </Box>
          )}
        </Typography>
      </Box>
      {units > 1 && (
        <Box sx={RESULT_ROW_SX}>
          <Typography sx={{ fontSize: '12px', color: '#464A54' }}>
            Fördersatz weitere WE:
          </Typography>
          <Typography sx={{ fontSize: '12px', fontWeight: 500 }}>{baseRate} %</Typography>
        </Box>
      )}
      <Box sx={RESULT_ROW_SX}>
        <Typography sx={{ fontSize: '12px', color: '#464A54' }}>Förderfähige Kosten:</Typography>
        <Typography sx={{ fontSize: '12px', fontWeight: 500 }}>
          {formatEuro(eligibleCosts)}{' '}
          {eligibleCosts < cost && (
            <Box component="span" sx={{ fontSize: '10px', color: '#6A6E78' }}>
              (max. {formatEuro(maxEligibleCosts)})
            </Box>
          )}
        </Typography>
      </Box>

      <Box
        sx={{
          ...RESULT_ROW_SX,
          borderTop: '1px solid #E1E1E4',
          paddingTop: '10px',
          marginTop: '10px',
          marginBottom: units > 1 ? 0 : '12px',
        }}
      >
        <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>Gesamte Förderung</Typography>
        <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>{formatEuro(funding)}</Typography>
      </Box>
      {units > 1 && (
        <Typography
          sx={{ fontSize: '9px', color: '#6A6E78', lineHeight: '14px', marginBottom: '12px' }}
        >
          davon 1. WE {formatEuro(fundingFirstUnit)} · weitere WE {formatEuro(fundingFurtherUnits)}.{' '}
          {bonusHint}
        </Typography>
      )}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          backgroundColor: '#E5F5E7',
          borderRadius: '12px',
          padding: '10px 12px',
        }}
      >
        <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>Kosten nach Förderung</Typography>
        <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>
          {formatEuro(costsAfterFunding)}
        </Typography>
      </Box>
    </Box>
  )
}

export default HeatpumpCalculatorCard
